import { useMemo } from 'react'
import { Label } from 'flowbite-react'
import Select, { type MultiValue } from 'react-select'
import { type AvailableExportGame } from '@app-types/api/exports'
import { type GameOption } from '@app-types/exports/options'

interface GamesMultiSelectProps {
  availableGames: AvailableExportGame[]
  games: string[]
  onChange: (next: string[]) => void
}

function GamesMultiSelect({
  availableGames,
  games,
  onChange,
}: GamesMultiSelectProps) {
  const gameOptions = useMemo<GameOption[]>(
    () =>
      availableGames.map((game) => ({
        label: game.title || game.slug,
        value: game.slug,
      })),
    [availableGames],
  )

  const selectedOptions = useMemo<GameOption[]>(
    () => gameOptions.filter((option) => games.includes(option.value)),
    [gameOptions, games],
  )

  const selectedTitle = useMemo(() => {
    if (!games.length) {
      return 'Игры не выбраны'
    }

    return selectedOptions.map((option) => option.label).join(', ')
  }, [games.length, selectedOptions])

  return (
    <div className="lg:col-span-3">
      <Label
        className="mb-2 block text-sm font-semibold text-slate-950 dark:text-slate-100"
        htmlFor="export-games"
      >
        Игры
      </Label>

      <Select<GameOption, true>
        className="text-sm"
        classNamePrefix="exports-rs"
        closeMenuOnSelect={false}
        inputId="export-games"
        instanceId="export-games"
        isMulti
        isSearchable
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        noOptionsMessage={() => 'Ничего не найдено'}
        onChange={(selected: MultiValue<GameOption>) => {
          onChange(selected.map((option) => option.value))
        }}
        options={gameOptions}
        placeholder="Выберите игры..."
        value={selectedOptions}
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: 42,
            borderColor: state.isFocused ? '#8b5cf6' : '#e2e8f0',
            borderRadius: 6,
            boxShadow: state.isFocused ? '0 0 0 3px rgb(139 92 246 / 0.15)' : 'none',
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 70,
          }),
        }}
      />

      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        {selectedTitle}
      </p>
    </div>
  )
}

export default GamesMultiSelect
